import React, { useState } from "react";
import { Star, Share2, Bug, Play, X, Lightbulb } from "lucide-react";
import * as icons from "react-icons/fa";
import data from "../../data/tools.json";
import { motion } from "framer-motion";
import { useTheme } from "../../context/ThemeContext";

const HeaderSection = ({ toolId }) => {
    useTheme();
    const [showReportModal, setShowReportModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showSuggestionModal, setShowSuggestionModal] = useState(false);
    const [bugTitle, setBugTitle] = useState("");
    const [bugDescription, setBugDescription] = useState("");
    const [suggestionTitle, setSuggestionTitle] = useState("");
    const [suggestionDescription, setSuggestionDescription] = useState("");

    const currentURL = window.location.href;

    // Find the tool data by ID
    const toolData = data.tools[toolId];
    if (!toolData || !toolData.headerSection) {
        console.error(`Tool data not found for toolId: ${toolId}`);
        return null;
    }

    const { headerSection: headerData } = toolData;

    const handleShareClick = () => {
        setShowShareModal(true);
        navigator.clipboard.writeText(currentURL);
    };

    const handleReportSubmit = (e) => {
        e.preventDefault();
        console.log("Bug reported:", { bugTitle, bugDescription });
        setShowReportModal(false);
        setBugTitle("");
        setBugDescription("");
    };

    const handleSuggestionSubmit = (e) => {
        e.preventDefault();
        console.log("Suggestion submitted:", { suggestionTitle, suggestionDescription });
        setShowSuggestionModal(false);
        setSuggestionTitle("");
        setSuggestionDescription("");
    };

    return (
        <>
            <header className="max-w-4xl m-4  lg:max-w-full lg:px-4 bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-4 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
                        <div className="flex items-center">
                            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white">
                                {headerData.title}
                            </h1>
                            {headerData.badge && (
                                <span className="ml-2 md:ml-4 px-2 py-0.5 md:px-3 md:py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-xs md:text-sm font-medium rounded-full">
                                    {headerData.badge}
                                </span>
                            )}
                        </div>

                        <div className="tool-actions flex flex-wrap gap-2 md:gap-3">
                            <button
                                onClick={() => setShowSuggestionModal(true)}
                                className="flex items-center gap-1 md:gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-800 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400 transition text-sm md:text-base"
                            >
                                <Lightbulb size={14} className="text-blue-600 dark:text-blue-400" />
                                <span className="hidden sm:inline">Suggest</span>
                            </button>

                            <button
                                className="flex items-center gap-1 md:gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-800 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400 transition text-sm md:text-base"
                                onClick={() => setShowReportModal(true)}
                            >
                                <Bug size={14} className="text-red-600 dark:text-red-400" />
                                <span className="hidden sm:inline">Report</span>
                            </button>
                            <button
                                onClick={handleShareClick}
                                className="flex items-center gap-1 md:gap-2 rounded-xl bg-[#70E4A8] hover:bg-[#5DCA95] text-white text-xs md:text-sm font-medium px-3 py-1.5 md:px-5 md:py-2.5 transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5DCA95] dark:focus:ring-offset-gray-900"
                            >
                                <Share2 size={14} className="text-white" />
                                <span className="hidden sm:inline">Share</span>
                            </button>
                        </div>
                    </div>

                    <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 mb-4 md:mb-6">
                        {headerData.description}
                    </p>

                    {/* Quick Guide section */}
                    {headerData.quickGuide && (
                        <motion.div
                            className="bg-gray-50 dark:bg-gray-700 p-4 md:p-6 mt-4 md:mt-6 rounded-xl border border-gray-200 dark:border-gray-600 w-full"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            <div className="flex items-start">
                                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-600 dark:text-indigo-300">
                                    <icons.FaLightbulb className="text-sm md:text-lg" />
                                </div>
                                <div className="ml-3 md:ml-4">
                                    <h3 className="font-bold text-gray-800 dark:text-white text-base md:text-lg mb-1 md:mb-2">
                                        {headerData.quickGuide.title}
                                    </h3>
                                    <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-1 text-xs md:text-sm">
                                        {headerData.quickGuide.steps.map(
                                            (step, index) => (
                                                <li
                                                    key={index}
                                                    className="hover:text-gray-800 dark:hover:text-white transition-colors"
                                                >
                                                    {step}
                                                </li>
                                            )
                                        )}
                                    </ol>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </div>
            </header>

            {/* Suggestion Modal */}
            {showSuggestionModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
                        <div className="p-4 md:p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg md:text-xl font-bold text-gray-800 dark:text-white">
                                    Suggest an Improvement
                                </h3>
                                <button
                                    onClick={() => setShowSuggestionModal(false)}
                                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSuggestionSubmit}>
                                <div className="mb-3 md:mb-4">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Title
                                    </label>
                                    <input
                                        type="text"
                                        value={suggestionTitle}
                                        onChange={(e) =>
                                            setSuggestionTitle(e.target.value)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm md:text-base"
                                        placeholder="Brief description of your suggestion"
                                        required
                                    />
                                </div>

                                <div className="mb-3 md:mb-4">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Description
                                    </label>
                                    <textarea
                                        rows="4"
                                        value={suggestionDescription}
                                        onChange={(e) =>
                                            setSuggestionDescription(e.target.value)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm md:text-base"
                                        placeholder="Detailed explanation of your suggestion"
                                        required
                                    />
                                </div>

                                <div className="mb-4 md:mb-6">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Supporting Images (optional)
                                    </label>
                                    <div className="flex items-center justify-center w-full">
                                        <label className="flex flex-col items-center justify-center w-full h-28 md:h-32 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600">
                                            <div className="flex flex-col items-center justify-center pt-4 pb-5 md:pt-5 md:pb-6">
                                                <icons.FaCloudUploadAlt className="text-gray-400 mb-1 md:mb-2 text-xl md:text-2xl" />
                                                <p className="mb-1 md:mb-2 text-xs md:text-sm text-gray-500 dark:text-gray-400">
                                                    <span className="font-semibold">
                                                        Click to upload
                                                    </span>{" "}
                                                    or drag and drop
                                                </p>
                                                <p className="text-xxs md:text-xs text-gray-500 dark:text-gray-400">
                                                    PNG, JPG, GIF (MAX. 5MB)
                                                </p>
                                            </div>
                                            <input
                                                type="file"
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-2 md:space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowSuggestionModal(false)}
                                        className="px-3 py-1.5 md:px-4 md:py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm md:text-base"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-3 py-1.5 md:px-4 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm md:text-base"
                                    >
                                        Submit
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Report Bug Modal */}
            {showReportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
                        <div className="p-4 md:p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg md:text-xl font-bold text-gray-800 dark:text-white">
                                    Report a Bug
                                </h3>
                                <button
                                    onClick={() => setShowReportModal(false)}
                                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleReportSubmit}>
                                <div className="mb-3 md:mb-4">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Title
                                    </label>
                                    <input
                                        type="text"
                                        value={bugTitle}
                                        onChange={(e) =>
                                            setBugTitle(e.target.value)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-sm md:text-base"
                                        placeholder="Brief description of the bug"
                                        required
                                    />
                                </div>

                                <div className="mb-3 md:mb-4">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Description
                                    </label>
                                    <textarea
                                        rows="4"
                                        value={bugDescription}
                                        onChange={(e) =>
                                            setBugDescription(e.target.value)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-sm md:text-base"
                                        placeholder="Detailed description of the bug"
                                        required
                                    />
                                </div>

                                <div className="mb-4 md:mb-6">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Screenshot (optional)
                                    </label>
                                    <div className="flex items-center justify-center w-full">
                                        <label className="flex flex-col items-center justify-center w-full h-28 md:h-32 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600">
                                            <div className="flex flex-col items-center justify-center pt-4 pb-5 md:pt-5 md:pb-6">
                                                <icons.FaCloudUploadAlt className="text-gray-400 mb-1 md:mb-2 text-xl md:text-2xl" />
                                                <p className="mb-1 md:mb-2 text-xs md:text-sm text-gray-500 dark:text-gray-400">
                                                    <span className="font-semibold">
                                                        Click to upload
                                                    </span>{" "}
                                                    or drag and drop
                                                </p>
                                                <p className="text-xxs md:text-xs text-gray-500 dark:text-gray-400">
                                                    PNG, JPG, GIF (MAX. 5MB)
                                                </p>
                                            </div>
                                            <input
                                                type="file"
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-2 md:space-x-3">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowReportModal(false)
                                        }
                                        className="px-3 py-1.5 md:px-4 md:py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-sm md:text-base"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-3 py-1.5 md:px-4 md:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm md:text-base"
                                    >
                                        Report
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Share Modal */}
            {showShareModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
                        <div className="p-4 md:p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg md:text-xl font-bold text-gray-800 dark:text-white">
                                    Share This Tool
                                </h3>
                                <button
                                    onClick={() => setShowShareModal(false)}
                                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Share Link
                                </label>
                                <div className="flex">
                                    <input
                                        type="text"
                                        value={currentURL}
                                        readOnly
                                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-xs md:text-sm"
                                    />
                                    <button
                                        onClick={handleShareClick}
                                        className="px-3 py-2 bg-indigo-600 text-white rounded-r-lg hover:bg-indigo-700 text-xs md:text-sm"
                                    >
                                        <icons.FaCopy className="inline mr-1" />
                                        Copy
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Share Via
                                </label>
                                <div className="flex space-x-2 md:space-x-3">
                                    <a
                                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentURL)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-blue-500 text-white hover:bg-blue-600 text-sm md:text-base"
                                    >
                                        <icons.FaFacebookF />
                                    </a>
                                    <a
                                        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(currentURL)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-blue-400 text-white hover:bg-blue-500 text-sm md:text-base"
                                    >
                                        <icons.FaTwitter />
                                    </a>
                                    <a
                                        href={`mailto:?subject=Check%20this%20tool&body=${encodeURIComponent(currentURL)}`}
                                        className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 text-sm md:text-base"
                                    >
                                        <icons.FaGoogle />
                                    </a>
                                    <a
                                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentURL)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-blue-700 text-white hover:bg-blue-800 text-sm md:text-base"
                                    >
                                        <icons.FaLinkedinIn />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default HeaderSection;